// apps/admin/src/lib/env-badge.js
//
// Sidebar-footer environment badge (Task 1.7; spec §3.1 decision 11).
// Deliberately its own tiny, side-effect-free module rather than living in
// main.js: main.js registers routes and calls boot() at import time (which
// fetches /api/admin/check and can redirect the page), so importing it from
// a unit test would fire all of that. Keeping resolveEnv/renderEnvBadge here
// means they can be exercised in isolation.
//
// resolveEnv() matches the LOWERCASED hostname against an EXACT-string
// allowlist — never substring/`includes` — so a look-alike host such as
// `retroplay.app.evil.com` or `evil-retroplay.app.attacker.com` resolves
// 'unknown', never 'prod'. There is no 'staging' state: the 2026-08-29 audit
// (Task 1.7 brief) found a single Azure Container App (`nesplay` /
// `nesplay-rg`) with no separate staging environment. The Container Apps
// default FQDN (*.azurecontainerapps.io) is intentionally NOT allowlisted —
// it isn't a user-facing origin, so it correctly falls through to 'unknown'.
const ENV_HOSTS = {
  'retroplay.app': 'prod',
  'www.retroplay.app': 'prod',
  'localhost': 'local',
  '127.0.0.1': 'local',
};

export function resolveEnv(hostname) {
  return ENV_HOSTS[String(hostname).toLowerCase()] ?? 'unknown';
}

const ENV_LABELS = { prod: 'Prod', local: 'Local' };
const ENV_CLASSES = ['is-prod', 'is-local', 'is-unknown'];

// Renders the `#admin-env-badge` chip (apps/admin/index.html's
// `.sidebar-foot`) for a hostname. The hostname is passed in explicitly
// (callers read `location.hostname`) rather than read here, so this stays
// testable without stubbing `window.location`. The signed-in admin's
// username is intentionally omitted — the only identity endpoint
// (`GET /api/admin/check`) returns `{isAdmin}` and no username (spec §2: not
// worth a new/modified endpoint) — so the footer shows env + role only.
export function renderEnvBadge(hostname, root = document) {
  const badge = root.querySelector('#admin-env-badge');
  if (!badge) return;

  const env = resolveEnv(hostname);
  badge.classList.remove(...ENV_CLASSES);
  badge.classList.add(`is-${env}`);
  // Unknown hosts show the hostname alongside the neutral state rather than
  // being mislabelled production (spec §3.1).
  badge.textContent = env === 'unknown' ? `Unknown (${hostname})` : ENV_LABELS[env];
}
